import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Menu, ChevronDown } from 'lucide-react';

const navLinks = [
  { name: 'Home', href: '/' },
  { 
    name: 'Services', 
    href: '/#services',
    dropdown: [
      { name: 'Google Ads', href: '/services/google-ads' },
      { name: 'Google Tag Manager', href: '/services/google-tag-manager' },
      { name: 'Google Analytics', href: '/services/google-analytics' },
    ]
  },
  { name: 'About', href: '/#about' },
  { name: 'Contact', href: '/#contact' },
];

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href) => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
    
    if (href.startsWith('/#')) {
      const id = href.replace('/#', '');
      
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const element = document.getElementById(id);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  return (
    <nav 
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{ backgroundColor: '#1a202c' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 group"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_booking-marketyze/artifacts/zx1mwvxg_Marketyze%20white%20logo%20%283%29.png"
              alt="MARKETYZE - Google Ads & Analytics Agency"
              width="180"
              height="48"
              className="h-10 lg:h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <div 
                key={link.name}
                className="relative"
                onMouseEnter={() => link.dropdown && setOpenDropdown(link.name)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <a
                  href={link.href}
                  onClick={(e) => {
                    if (link.href.startsWith('/#')) {
                      e.preventDefault();
                      handleNavClick(link.href);
                    }
                  }}
                  className="text-sm font-medium text-white/80 hover:text-white transition-colors duration-200 flex items-center gap-1"
                >
                  {link.name}
                  {link.dropdown && <ChevronDown className="h-4 w-4" />}
                </a>
                
                {/* Dropdown Menu */}
                {link.dropdown && openDropdown === link.name && (
                  <div className="absolute top-full left-0 pt-2 w-56">
                    <div className="bg-card border border-border rounded-xl shadow-xl overflow-hidden">
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.name}
                          to={item.href}
                          className="flex items-center gap-3 px-4 py-3 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
                          onClick={() => {
                            setOpenDropdown(null);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                        >
                          <img 
                            src={
                              item.name === 'Google Ads' 
                                ? 'https://upload.wikimedia.org/wikipedia/commons/c/c7/Google_Ads_logo.svg'
                                : item.name === 'Google Tag Manager'
                                ? 'https://www.gstatic.com/analytics-suite/header/suite/v2/ic_tag_manager.svg'
                                : 'https://www.gstatic.com/analytics-suite/header/suite/v2/ic_analytics.svg'
                            }
                            alt={item.name}
                            className="w-5 h-5"
                          />
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <Link to="/booking" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <Button className="bg-[#EA4335] hover:bg-[#EA4335]/90 text-white rounded-full px-6">
                Book Free Audit
              </Button>
            </Link>
          </div>

          {/* Mobile Menu */}
          <div className="lg:hidden">
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-10 w-10 text-white hover:bg-white/10">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[350px] bg-card">
                <div className="flex flex-col gap-4 mt-8">
                  {navLinks.map((link) => (
                    <div key={link.name}>
                      <a
                        href={link.href}
                        onClick={(e) => {
                          if (link.href.startsWith('/#')) {
                            e.preventDefault();
                          }
                          handleNavClick(link.href);
                        }}
                        className="text-lg font-medium text-foreground hover:text-google-blue transition-colors duration-200 py-2 block"
                      >
                        {link.name}
                      </a>
                      {/* Mobile dropdown items */}
                      {link.dropdown && (
                        <div className="pl-4 space-y-2 mt-2">
                          {link.dropdown.map((item) => (
                            <Link
                              key={item.name}
                              to={item.href}
                              className="flex items-center gap-3 text-sm text-muted-foreground hover:text-google-blue py-1"
                              onClick={() => {
                                setIsMobileMenuOpen(false);
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                              }}
                            >
                              <img 
                                src={
                                  item.name === 'Google Ads' 
                                    ? 'https://upload.wikimedia.org/wikipedia/commons/c/c7/Google_Ads_logo.svg'
                                    : item.name === 'Google Tag Manager'
                                    ? 'https://www.gstatic.com/analytics-suite/header/suite/v2/ic_tag_manager.svg'
                                    : 'https://www.gstatic.com/analytics-suite/header/suite/v2/ic_analytics.svg'
                                }
                                alt={item.name}
                                className="w-5 h-5"
                              />
                              {item.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  <div className="pt-4 border-t border-border">
                    <Link to="/booking" onClick={() => {
                      setIsMobileMenuOpen(false);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}>
                      <Button className="w-full bg-[#EA4335] hover:bg-[#EA4335]/90 text-white rounded-full">
                        Book Free Audit
                      </Button>
                    </Link>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};
